import { createElement, useMemo } from "react";
import { AgGrid } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { LoadingCellRenderer } from "~/pages/Dashboard/components/WorkspaceTable/CellComponents";
import { useAgGridUtils } from "./gridUtils";
import { renderReactNodeToHtml } from "./renderReactNodeToHtml";
import type { ColDef, GridOptions } from "ag-grid-community";

interface UseAgGridDefaultsProps {
  overrides?: Partial<GridOptions>;
  defaultColDefOverrides?: Partial<ColDef>;
  defaultSideBarDefOverrides?: Partial<AgGrid.SideBarDef>;
}

const bulkActionsIcon = renderReactNodeToHtml(createElement(Icon, { type: "check-square" }));

export const useAgGridDefaults = ({
  overrides = {},
  defaultColDefOverrides = {},
  defaultSideBarDefOverrides = {},
}: UseAgGridDefaultsProps = {}) => {
  const { onGridSizeChanged } = useAgGridUtils();
  // Default Column Def
  const defaultColDef: ColDef = useMemo(() => {
    return {
      flex: 1,
      resizable: true,
      sortable: true,
      filter: true,
      menuTabs: ["generalMenuTab", "filterMenuTab", "columnsMenuTab"],
      ...defaultColDefOverrides,
    };
  }, [defaultColDefOverrides]);

  // Default Sidebar Def
  const defaultSideBarDef: AgGrid.SideBarDef = useMemo(
    () => ({
      position: "right",
      toolPanels: [
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          toolPanelParams: {
            suppressFilterSearch: true,
            suppressColumnFilter: false,
          },
        },
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
          },
        },
      ],
      ...defaultSideBarDefOverrides,
    }),
    [defaultSideBarDefOverrides],
  );

  const customIcons = useMemo(() => {
    return {
      bulkActions: bulkActionsIcon,
    };
  }, []);

  const gridOptions: GridOptions = useMemo(
    () => ({
      autoSizeStrategy: { type: "fitGridWidth" },
      domLayout: "normal",
      rowHeight: 42,
      suppressCellFocus: true,
      pagination: true,
      paginationPageSize: 20,
      paginationPageSizeSelector: [20, 50, 100],
      suppressRowClickSelection: true,
      overlayNoRowsTemplate: "No rows found",
      rowModelType: "clientSide",
      sideBar: defaultSideBarDef,
      defaultColDef,
      loadingCellRenderer: LoadingCellRenderer,
      suppressContextMenu: true,
      onGridSizeChanged,
      icons: customIcons,
      ...overrides,
    }),
    [overrides, defaultColDef, defaultSideBarDef, onGridSizeChanged, customIcons],
  );

  return { gridOptions };
};
